import React, { PureComponent, Fragment } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
} from "recharts";
import {
  selectFormat,
  StandardTooltip,
  CustomizedYAxisTick,
  MAIN_COLORS,
  formatDateFromData,
  isDateFromDataInISOFormat,
  getAllKeys,
} from "./../Utils/Utils.js";

class AuditBoard extends PureComponent {
  componentDidMount() {
    this.props.childRef(this);
  }

  renderBars = (data) => {
    const sortedKeys = this.getSortedKeys(data);

    for (let i = 0; i <= data.length; i++) {
      let idx = 0;
      return sortedKeys.map((source, key) => {
        return <Bar key={key} dataKey={source} fill={MAIN_COLORS[idx++]} />;
      });
    }
  };

  legendElement = (data) => {
    return data.map((entry, i) => {
      return (
        <span key={i} className="legend mt-2 mt-md-0">
          <span
            className="legend__icon"
            style={{ background: MAIN_COLORS[i] }}
          ></span>
          <span className="legend__text" style={{ color: MAIN_COLORS[i] }}>
            {entry}{" "}
          </span>
        </span>
      );
    });
  };

  renderLegends = (data) => {
    let legends = [];
    let all_legends = [];

    const sortedKeys = this.getSortedKeys(data);

    for (let i = 0; i <= data.length - 1; i++) {
      sortedKeys.forEach((source) => all_legends.push(source));
    }

    legends = [...new Set(all_legends)];
    return this.legendElement(legends);
  };

  getSortedKeys(_data) {
    if (!_data) return [];
    if (!_data[0]) return [];
    const data = _data.filter((d) => d);

    const keys = getAllKeys(data, ["date", "full_date", "metricName"]);
    const len = data.length;

    const mean = keys.map((key) => {
      return {
        key: key,
        value:
          data.reduce((accum, curr) => {
            return accum + (curr[key] || 0);
          }, 0) / len,
      };
    });

    return mean.sort((a, b) => b.value - a.value).map((d) => d.key);
  }

  getExportData = () => ({
    data: this.props.data.graph,
    opts: {
      type: "chart",
      exclude: ["date", "full_date", "metricName"],
      date: {
        // overwrites the default settings for date only when the date is in ISO format
        defined: isDateFromDataInISOFormat(this.props.data.graph),
        format: "YYYY-MM-DDThh:mm:ssZ",
        viewFormat: "YYYY-MM-DD",
      },
    },
  });

  render() {
    let preparedData = formatDateFromData(this.props.data.graph, "YYYY/MM/DD");
    return (
      <Fragment>
        {this.renderLegends(preparedData)}
        <ResponsiveContainer height={220} width="100%">
          <BarChart
            data={preparedData}
            margin={{ top: 20, right: 0, left: 0, bottom: 0 }}
          >
            <Tooltip
              cursor={{ fill: "#eee", opacity: 0.5 }}
              content={
                <StandardTooltip
                  render={({ data }) => {
                    const payload = data(this.props);
                    if (payload !== null) {
                      const tooltip_data = payload[0].payload;
                      return (
                        <div className="standard-tooltip">
                          {tooltip_data.date && (
                            <header className="standard-tooltip__header">
                              <span className="standard-tooltip__key-header">
                                Date:
                              </span>
                              <span className="standard-tooltip__value-header">
                                {tooltip_data.date}
                              </span>
                            </header>
                          )}
                          <main className="standard-tooltip__main">
                            {Object.keys(payload)
                              .sort(
                                (a, b) => payload[b].value - payload[a].value
                              )
                              .map((key, index) => (
                                <div
                                  className="standard-tooltip__item"
                                  key={key}
                                >
                                  <span
                                    className="standard-tooltip__key"
                                    style={{
                                      color: payload[key].color,
                                      textTransform: "capitalize",
                                    }}
                                  >
                                    <span
                                      className="standard-tooltip__icon"
                                      style={{ background: payload[key].color }}
                                    ></span>
                                    {payload[key].dataKey}{" "}
                                  </span>
                                  <span className="standard-tooltip__value">
                                    {selectFormat("number", payload[key].value)}
                                  </span>
                                </div>
                              ))}
                          </main>
                        </div>
                      );
                    }
                  }}
                />
              }
            />

            <CartesianGrid
              stroke="#FEE5DE"
              strokeDasharray="3 3"
              vertical={false}
            />
            {this.renderBars(preparedData)}
            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              tick={{ strokeWidth: 1, fill: "#25263F" }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              mirror={true}
              tick={<CustomizedYAxisTick />}
            />
          </BarChart>
        </ResponsiveContainer>
      </Fragment>
    );
  }
}

export default AuditBoard;
